import { ValueMapMode } from '../../enums';
import { BooleanAttribute, BOOLEAN_ATTRIBUTE_TYPE } from './BooleanAttribute';
import type { NumberMetadataKeys } from './NumberAttribute';
import { NumberAttribute, NUMBER_ATTRIBUTE_TYPE } from './NumberAttribute';
import type { StringOrNumberMetadataKeys } from './StringOrNumberAttribute';
import { StringOrNumberAttribute, INFERRED_NUMBER_ATTRIBUTE_TYPE } from './StringOrNumberAttribute';
import { FontAttribute, templateFontsByIndex, FONT_ATTRIBUTE_TYPE } from './template/FontAttribute';
import {
    ColorAttribute,
    templateColorByIndex,
    COLOR_ATTRIBUTE_TYPE,
} from './template/ColorAttribute';
import {
    AlphaAttribute,
    templateAlphaByIndex,
    ALPHA_ATTRIBUTE_TYPE,
} from './template/AlphaAttribute';
import {
    WatermarkAttribute,
    templateWatermarkSourcesByIndex,
    WATERMARK_ATTRIBUTE_TYPE,
} from './template/WatermarkAttribute';
import type { StringMetadataKeys } from './StringAttribute';
import { StringAttribute, STRING_ATTRIBUTE_TYPE } from './StringAttribute';
import { UrlAttribute, URL_ATTRIBUTE_TYPE } from './UrlAttribute';
import type { CommonMetadataKeys } from './common';
import {
    ATTRIBUTE_TYPE_METADATA_KEY,
    SERIALIZE_METADATA_KEY,
    ANIMATABLE_METADATA_KEY,
    ALL_METADATA_KEY,
} from './common';
import {
    getTemplateIndexFromAssetProperty,
    getTemplateIndex,
    getTemplateProperty,
    isTemplateReference,
    getTemplateValueAttribute,
} from './template/common';

type AllAttributeTypes =
    | typeof BOOLEAN_ATTRIBUTE_TYPE
    | typeof NUMBER_ATTRIBUTE_TYPE
    | typeof COLOR_ATTRIBUTE_TYPE
    | typeof ALPHA_ATTRIBUTE_TYPE
    | typeof STRING_ATTRIBUTE_TYPE
    | typeof URL_ATTRIBUTE_TYPE;

/**
 * Generic metadata retrieval function with prototype chain lookup (up to 1 hop)
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getMetadata<T, P extends Extract<keyof T, string>, K extends string>(
    metadataKey: K,
    target: T,
    propertyKey: P,
) {
    const metadata = target.constructor[Symbol.for('Symbol.metadata')];
    if (metadata && metadata[propertyKey]) {
        return metadata[propertyKey][metadataKey];
    }

    const parentConstructor = Object.getPrototypeOf(target.constructor);
    if (parentConstructor && parentConstructor !== Function.prototype) {
        const parentMetadata = parentConstructor[Symbol.for('Symbol.metadata')];
        if (parentMetadata && parentMetadata[propertyKey]) {
            return parentMetadata[propertyKey][metadataKey];
        }
    }

    return undefined;
}

/**
 * Get a specific piece of metadata for a boolean attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getBooleanMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: CommonMetadataKeys,
    target: T,
    propertyKey: T[P] extends boolean ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Get a specific piece of metadata for a string attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getStringMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: StringMetadataKeys,
    target: T,
    propertyKey: T[P] extends string ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Get a specific piece of metadata for a number attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getNumberMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: NumberMetadataKeys,
    target: T,
    propertyKey: T[P] extends number ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Get a specific piece of metadata for an inferred number attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getStringOrNumberMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: StringOrNumberMetadataKeys,
    target: T,
    propertyKey: T[P] extends string | number ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Get a specific piece of metadata for a url attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getUrlMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: CommonMetadataKeys,
    target: T,
    propertyKey: T[P] extends string ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Get a specific piece of metadata for a color attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getColorMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: CommonMetadataKeys,
    target: T,
    propertyKey: T[P] extends number ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Get a specific piece of metadata for an alpha attribute
 * @param metadataKey - the key of the metadata stored on the target's property
 * @param target - the object from which to retrieve the metadata
 * @param propertyKey - the property key of the object from which to retrieve the metadata
 * @returns the value stored at the metadata key
 */
function getAlphaMetadata<T, P extends Extract<keyof T, string>>(
    metadataKey: CommonMetadataKeys,
    target: T,
    propertyKey: T[P] extends number ? P : never,
) {
    return getMetadata(metadataKey, target, propertyKey);
}

/**
 * Determine the type of the attribute which the property key references on the target object
 * @param target - the object for which to determine the attribute type
 * @param propertyKey - the property key of the object for which to determine the attribute type
 * @returns the attribute type if the key references an attribute, otherwise returns undefined
 */
function getAttributeType<T, P extends Extract<keyof T, string>>(
    target: T,
    propertyKey: T[P] extends any ? P : never,
): AllAttributeTypes | undefined {
    return getMetadata(ATTRIBUTE_TYPE_METADATA_KEY, target, propertyKey);
}

/**
 * Generic function to get attribute arrays with inheritance support (up to 1 hop)
 * @param target - the object from which to retrieve the attributes
 * @param metadataKey - the metadata key to look for
 * @returns the list of attributes from the specified metadata key
 */
function getAttributeArray<T extends Object, R>(target: T, metadataKey: string): Array<R> {
    const result = [];

    const metadata = target.constructor[Symbol.for('Symbol.metadata')];
    if (metadata && metadata[metadataKey]) {
        result.push(...metadata[metadataKey]);
    }

    const parentConstructor = Object.getPrototypeOf(target.constructor);
    if (parentConstructor && parentConstructor !== Function.prototype) {
        const parentMetadata = parentConstructor[Symbol.for('Symbol.metadata')];
        if (parentMetadata && parentMetadata[metadataKey]) {
            result.push(...parentMetadata[metadataKey]);
        }
    }

    return result;
}

/**
 * For the provided object, get all attributes which should be serialized. These attributes
 * should be included in [propertiesToSerialize]{@link SerializableMixin#propertiesToSerialize}
 * @param target - the object from which to retrieve the serializable attributes
 * @returns the list of serializable attributes
 */
function getAttributesToSerialize<T extends Object>(target: T): Array<string> {
    return getAttributeArray<T, string>(target, SERIALIZE_METADATA_KEY);
}

/**
 * For the provided object, get all attributes which are keyable. These attributes
 * should be included in [propertiesToKey]{@link SerializableMixin#propertiesToKey}
 * @param target - the object from which to retrieve the keyable attributes
 * @returns the list of keyable attributes
 */
function getAnimatableAttributes<T extends Object>(target: T): Array<keyof T> {
    return getAttributeArray<T, keyof T>(target, ANIMATABLE_METADATA_KEY);
}

/**
 * For the provided object, get all attributes
 * @param target - the object from which to retrieve the attributes
 * @returns the list of all attributes registered on the target
 */
function getAllAttributes<T extends Object>(target: T): Array<Extract<keyof T, string>> {
    return getAttributeArray<T, Extract<keyof T, string>>(target, ALL_METADATA_KEY);
}

function getAllAttributesOfType(target, attributeTypes: string[]) {
    return getAllAttributes(target).filter((attribute) =>
        attributeTypes.includes(getAttributeType(target, attribute)),
    );
}

const TEMPLATE_ATTRIBUTE_TYPES = [
    COLOR_ATTRIBUTE_TYPE,
    ALPHA_ATTRIBUTE_TYPE,
    FONT_ATTRIBUTE_TYPE,
    WATERMARK_ATTRIBUTE_TYPE,
];

function getAllTemplateAttributes(target, valueMapMode = ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE) {
    const attributes = getAllAttributesOfType(target, TEMPLATE_ATTRIBUTE_TYPES);
    if (valueMapMode === ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE) {
        return attributes;
    }
    return attributes.filter((attr) => {
        const valueProp = getTemplateValueAttribute(target, attr);
        return isTemplateReference(valueProp);
    });
}

function isTemplateAttribute(target, propertyKey) {
    const attributeType = getAttributeType(target, propertyKey);
    return TEMPLATE_ATTRIBUTE_TYPES.includes(attributeType);
}

export {
    BooleanAttribute,
    NumberAttribute,
    FontAttribute,
    ColorAttribute,
    AlphaAttribute,
    WatermarkAttribute,
    StringAttribute,
    StringOrNumberAttribute,
    UrlAttribute,
    getBooleanMetadata,
    getNumberMetadata,
    getStringOrNumberMetadata,
    getColorMetadata,
    getAlphaMetadata,
    getAttributeType,
    getStringMetadata,
    getUrlMetadata,
    getAttributesToSerialize,
    getAnimatableAttributes,
    getAllAttributesOfType,
    getAllTemplateAttributes,
    isTemplateAttribute,
    getAllAttributes,
    BOOLEAN_ATTRIBUTE_TYPE,
    FONT_ATTRIBUTE_TYPE,
    ALPHA_ATTRIBUTE_TYPE,
    WATERMARK_ATTRIBUTE_TYPE,
    COLOR_ATTRIBUTE_TYPE,
    NUMBER_ATTRIBUTE_TYPE,
    INFERRED_NUMBER_ATTRIBUTE_TYPE,
    STRING_ATTRIBUTE_TYPE,
    URL_ATTRIBUTE_TYPE,
    templateAlphaByIndex,
    templateColorByIndex,
    templateFontsByIndex,
    templateWatermarkSourcesByIndex,
    getTemplateIndexFromAssetProperty,
    getTemplateIndex,
    getTemplateProperty,
    isTemplateReference,
    getTemplateValueAttribute,
};
